import api from './api';

class BookingService {
  // Create a new booking
  async createBooking(bookingData) {
    try {
      const response = await api.post('/public/bookings', bookingData);
      // Wrap the backend response in the expected structure
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating booking:', error);
      console.log('Error response:', error.response);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create booking'
      };
    }
  }

  // Get booking details by confirmation code
  async getBooking(confirmationCode) {
    try {
      const response = await api.get(`/public/bookings/${confirmationCode}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching booking:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch booking'
      };
    }
  }

  // Update booking (cancel, reschedule, or update info)
  async updateBooking(confirmationCode, updateData) {
    try {
      const response = await api.put(`/public/bookings/${confirmationCode}`, updateData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating booking:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update booking'
      };
    }
  }

  // Cancel a booking
  async cancelBooking(confirmationCode) {
    return this.updateBooking(confirmationCode, { action: 'cancel' });
  }

  // Reschedule a booking
  async rescheduleBooking(confirmationCode, newTimeSlotId) {
    return this.updateBooking(confirmationCode, { 
      action: 'reschedule', 
      newTimeSlotId 
    });
  }

  // Update booking information
  async updateBookingInfo(confirmationCode, userInfo) {
    return this.updateBooking(confirmationCode, { 
      action: 'update-info', 
      userInfo 
    });
  }

  // Get upcoming public events (bookings without personal info)
  async getUpcomingPublicEvents() {
    try {
      const response = await api.get('/public/events');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching upcoming public events:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch upcoming events'
      };
    }
  }

  // Admin methods - get booking statistics
  async getBookingStats() {
    try {
      console.log('Fetching booking stats...');
      const response = await api.get('/admin/bookings/stats');
      
      return {
        success: true,
        data: response.data // The backend returns { success: true, data: { activeBookings: ..., completedBookings: ... } }
      };
    } catch (error) {
      console.error('Error fetching booking stats:', error);
      console.log('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch booking statistics'
      };
    }
  }

  // Admin method - get all bookings with pagination and filters
  async getAllBookings(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filter params
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.eventTypeId) queryParams.append('eventTypeId', params.eventTypeId);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.includeArchived) queryParams.append('includeArchived', params.includeArchived);
      if (params.includeAllStatuses) queryParams.append('includeAllStatuses', params.includeAllStatuses);
      
      // Add sort params
      if (params.sortField) queryParams.append('sortField', params.sortField);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const url = `/admin/bookings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await api.get(url);
      
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error fetching bookings:', error);
      console.log('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch bookings'
      };
    }
  }

  // Admin method - update booking status
  async updateBookingStatus(bookingId, status) {
    try {
      const response = await api.put(`/admin/bookings/${bookingId}/status`, { status });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating booking status:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update booking status'
      };
    }
  }

  // Admin method - update booking details
  async updateBookingDetails(bookingId, updateData) {
    try {
      const response = await api.put(`/admin/bookings/${bookingId}`, updateData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating booking details:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update booking details'
      };
    }
  }

  // Admin method - archive booking
  async archiveBooking(bookingId) {
    try {
      const response = await api.put(`/admin/bookings/${bookingId}/archive`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error archiving booking:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to archive booking'
      };
    }
  }

  // Admin method - create booking (admin-initiated)
  async createAdminBooking(bookingData) {
    try {
      console.log('Creating admin booking with data:', bookingData);
      const response = await api.post('/admin/bookings', bookingData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating admin booking:', error);
      console.log('Error response:', error.response);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create booking'
      };
    }
  }

  // Admin method - get admin availability (includes booked slots for double-booking)
  async getAdminAvailability(eventTypeId, date) {
    try {
      const response = await api.get(`/admin/bookings/availability/${eventTypeId}?date=${date}`);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error fetching admin availability:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch availability'
      };
    }
  }

  // Admin method - toggle pre-registration for an event/booking
  async togglePreRegistration(bookingId, allowPreRegistration) {
    try {
      const response = await api.patch(`/admin/bookings/${bookingId}/pre-registration`, { 
        allowPreRegistration 
      });
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error toggling pre-registration:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to toggle pre-registration'
      };
    }
  }

  // Admin method - toggle booking visibility in the HCW mobile app.
  // When enabling, `details` carries the extra mobile-event fields collected
  // in the create/publish flow (payRate, payType, estimatedHours, storage units, etc.).
  async toggleAppVisibility(bookingId, appEnabled, details = {}) {
    try {
      const response = await api.patch(`/admin/bookings/${bookingId}/app-visibility`, {
        appEnabled,
        ...details
      });
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error toggling app visibility:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to toggle app visibility'
      };
    }
  }
}

export default new BookingService();
